import { useEffect, useState, useRef } from 'react';
import { useLocation, useRoute } from 'wouter';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';
import Cookies from 'js-cookie';
import { LanguagePill } from '@/components/chrome/LanguagePill';

export default function ConfirmEmailPage() {
  const { t } = useTranslation(['auth']);
  const [, params] = useRoute<{ userId: string; token: string }>('/confirm-email/:userId/:token');
  const [, setLocation] = useLocation();
  const { refreshUser } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Prevent multiple calls (React StrictMode in dev causes double-invocation)
    if (hasRunRef.current) return;

    const confirmEmail = async () => {
      if (!params?.userId || !params?.token) {
        setStatus('error');
        setMessage(t('auth:emailConfirmation.invalid'));
        return;
      }

      hasRunRef.current = true;

      try {
        const response = await apiClient.instance.get(`/v1/auth/confirm-email/${params.userId}/${params.token}`);

        // Store the tokens from the confirmation response
        if (response.data.data.access_token) {
          Cookies.set('access_token', response.data.data.access_token, {
            expires: 7,
            sameSite: 'Lax',
          });
        }

        if (response.data.data.refresh_token) {
          Cookies.set('refresh_token', response.data.data.refresh_token, {
            expires: 30,
            sameSite: 'Lax',
          });
        }

        setStatus('success');
        setMessage(t('auth:emailConfirmation.success'));

        // Refresh user data to get updated confirmation status
        await refreshUser();

        // Redirect to dashboard after 3 seconds with a full page reload to ensure fresh data
        setTimeout(() => {
          window.location.href = '/admin/dashboard';
        }, 3000);
      } catch (error: any) {
        console.error('Email confirmation error:', error);
        setStatus('error');

        const errorMessage = error.response?.data?.error?.message ||
                           error.response?.data?.message ||
                           t('auth:emailConfirmation.invalid');
        setMessage(errorMessage);
      }
    };

    confirmEmail();
  }, [params?.userId, params?.token, t, setLocation, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="fixed top-4 right-4">
        <LanguagePill />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <Loader2 className="h-16 w-16 text-purple-600 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="h-16 w-16 text-green-600" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-red-600" />
            )}
          </div>

          <CardTitle className="text-2xl">
            {status === 'loading' && t('auth:emailConfirmation.confirming')}
            {status === 'success' && t('auth:emailConfirmation.success')}
            {status === 'error' && t('auth:emailConfirmation.invalid')}
          </CardTitle>

          <CardDescription className="text-base mt-2">
            {message}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {status === 'success' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-sm text-green-800">
                  {t('auth:emailConfirmation.redirecting')}
                </p>
              </div>

              <Button
                onClick={() => window.location.href = '/admin/dashboard'}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {t('auth:emailConfirmation.goToDashboard')}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Button
                onClick={() => setLocation('/login')}
                className="w-full"
                variant="outline"
              >
                {t('auth:emailConfirmation.backToLogin')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
